import { useContext, useMemo } from "react"

import { DragOriginContext } from "shared/context/drag-and-drop/DragOriginContext"
import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { exists } from "shared/lib/exists"
import { DragSource } from "shared/types/drag-and-drop/DragSource"

import {
  UseDraggableArguments,
  useDraggable as useDndKitDraggable
} from "@dnd-kit/core"

interface UseDraggableArgsBase<Data = unknown>
  extends Omit<UseDraggableArguments, "data"> {
  data: Data
  type: string
  label?: string
  dragSourceID?: string
}

export type UseDraggableArgs<
  Data = unknown,
  Meta = unknown
> = unknown extends Meta
  ? UseDraggableArgsBase<Data> & { meta?: Meta }
  : UseDraggableArgsBase<Data> & { meta: Meta }

export function useDraggable<Data = unknown, Meta = unknown>({
  id,
  data,
  meta,
  label,
  type,
  dragSourceID,
  ...args
}: UseDraggableArgs<Data, Meta>) {
  const origin = useContext(DragOriginContext)
  const { dragSource: activeDragSource } = useContext(DragSourceDataContext)
  const dragSource = useMemo<DragSource<Data, Meta | undefined>>(
    () => ({
      id: id.toString(),
      meta,
      origin,
      data,
      label,
      type,
      dragSourceID
    }),
    [id, meta, origin, data, label, type, dragSourceID]
  )

  const draggableData = useDndKitDraggable({
    id,
    data: dragSource,
    ...args
  })

  const isDragging =
    (exists(activeDragSource?.dragSourceID) &&
      activeDragSource.dragSourceID === dragSourceID) ||
    draggableData.isDragging

  return { ...draggableData, isDragging }
}
